import { useCallback, useEffect, useState } from 'react';

/**
 * Detects when the server is serving a newer build than this tab is running.
 *
 * The service worker's own `needRefresh` is the primary signal, but it is not
 * sufficient: it only fires when the worker itself notices a new registration,
 * and an installed PWA can stay open for days without that happening. Comparing
 * build identities is a direct check that does not depend on worker lifecycle
 * at all — the running bundle knows which build it is, and `version.json` says
 * which build the server has.
 */

/** The build this bundle was produced by, injected by Vite. */
export const BUILD_ID: string = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev';

async function fetchServerBuildId(signal?: AbortSignal): Promise<string | null> {
  try {
    // `no-store` matters: a cached version.json would report this client's own
    // build back to it and no update would ever be seen.
    const response = await fetch('/version.json', { cache: 'no-store', signal });
    if (!response.ok) return null;

    const body = (await response.json()) as { buildId?: unknown };
    return typeof body.buildId === 'string' ? body.buildId : null;
  } catch {
    return null;
  }
}

export interface AppVersionState {
  /** True when the server reports a build different from this one. */
  outdated: boolean;
  checking: boolean;
  /** Null until a check has succeeded. */
  serverBuildId: string | null;
  /** Runs a check immediately; resolves to whether an update is available. */
  check: () => Promise<boolean>;
  /** Discards caches and reloads onto the new build. */
  reload: () => Promise<void>;
}

/** How often to poll while the app is open. */
const POLL_INTERVAL_MS = 30 * 60 * 1000;

export function useAppVersion(): AppVersionState {
  const [serverBuildId, setServerBuildId] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  const check = useCallback(async (): Promise<boolean> => {
    setChecking(true);
    try {
      const id = await fetchServerBuildId();
      if (id) setServerBuildId(id);
      return id != null && id !== BUILD_ID;
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const run = (): void => {
      void fetchServerBuildId(controller.signal).then((id) => {
        if (id) setServerBuildId(id);
      });
    };

    run();
    const timer = setInterval(run, POLL_INTERVAL_MS);

    // Returning to a backgrounded PWA is the moment an update is most likely
    // to be waiting, and the cheapest time to notice it.
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') run();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      controller.abort();
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const reload = useCallback(async (): Promise<void> => {
    // Drop the precache so the reload cannot be served the old bundle back.
    if ('caches' in window) {
      const keys = await caches.keys().catch(() => [] as string[]);
      await Promise.all(keys.map((key) => caches.delete(key))).catch(() => undefined);
    }

    const registrations = await navigator.serviceWorker?.getRegistrations().catch(() => []);
    await Promise.all((registrations ?? []).map((registration) => registration.update()));

    window.location.reload();
  }, []);

  return {
    outdated: serverBuildId != null && serverBuildId !== BUILD_ID,
    checking,
    serverBuildId,
    check,
    reload,
  };
}
